import { inject, Injectable } from '@angular/core';
import { IListing } from '@curbnturf/entities';
import { firstValueFrom } from 'rxjs';
import { AdminListingApiService } from '../api/admin-listing-api.service';

/** How long the download is reused before a visit fetches it again. */
export const ALL_LISTINGS_MAX_AGE_MS = 10 * 60_000;

/**
 * Every listing that isn't deleted, downloaded once (many pages) and shared by
 * Listing quality and Help them finish, so moving between them doesn't fetch
 * it all again. Visits at the same time share one download; a failed one isn't
 * kept. The listing editor calls `invalidate()` after a save, so the next visit
 * sees the change.
 */
@Injectable({ providedIn: 'root' })
export class AdminAllListingsService {
  private readonly api = inject(AdminListingApiService);

  private cached?: { at: number; listings: Promise<IListing[]> };

  /** When the listings shown were downloaded (ms), once they have been. */
  loadedAt?: number;

  /** The listings, from the last download if it's recent; `fresh` always downloads again. */
  get(fresh = false, now = Date.now()): Promise<IListing[]> {
    if (!fresh && this.cached && now - this.cached.at < ALL_LISTINGS_MAX_AGE_MS) return this.cached.listings;
    const listings = firstValueFrom(this.api.listAll({ deleted: false }));
    const entry = { at: now, listings };
    this.cached = entry;
    listings.then(
      () => {
        if (this.cached === entry) this.loadedAt = now;
      },
      () => {
        if (this.cached === entry) this.cached = undefined;
      },
    );
    return listings;
  }

  /** Forget the download, e.g. after a listing was edited. */
  invalidate(): void {
    this.cached = undefined;
  }
}
